import { useEffect, useState, useCallback } from 'react';
import {
  Building2, GraduationCap, AlertTriangle, CheckCircle2, BarChart3,
  Activity, Clock, TrendingUp, TrendingDown, Shield, Megaphone,
  Users, Landmark, Eye, ChevronDown, ChevronUp, RefreshCw, UserX,
} from 'lucide-react';
import { useDashboardApi, LoadingSpinner, ErrorPanel } from '../../components/dashboard/DashboardShared';
import { useAuthStore } from '../../store/authStore';
import { FamiliasPieChart, NotificacoesFamiliasChart } from './components/MuniChartsRow';
import { AlcancePoliticoCard } from './components/MuniAlcancePolitico';
import { PainelAlertaUrgente } from './components/MuniPainelUrgente';
import '../../components/ui/datatable.css';
import './municipal-dashboard.css';

/* ─── Hook: Live Status (heartbeat + countdown) ─── */
const ANALYTICS_INTERVAL = 5 * 60; // 5 min

function useLiveStatus() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return { isOnline };
}


/* ─── Sub-componentes locais ─── */

function HeroStat({ icon: Icon, value, label, color }: { icon: any; value: string | number; label: string; color: string }) {
  return (
    <div className="muni-hero-stat">
      <div className="muni-hero-stat-icon" style={{ background: `${color}20`, color }}>
        <Icon size={12} strokeWidth={2.25} />
      </div>
      <div>
        <div className="muni-hero-stat-value">{value}</div>
        <div className="muni-hero-stat-label">{label}</div>
      </div>
    </div>
  );
}

function PresencaGauge({ taxa }: { taxa: number }) {
  const r = 18;
  const circ = 2 * Math.PI * r;
  const offset = circ - (taxa / 100) * circ;
  const color = taxa >= 85 ? '#10b981' : taxa >= 70 ? '#f59e0b' : '#ef4444';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexShrink: 0, paddingLeft: 8, borderLeft: '1px solid rgba(255,255,255,0.12)' }}>
      <div style={{ position: 'relative', width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <svg viewBox="0 0 44 44" width="44" height="44">
          <circle cx="22" cy="22" r={r} fill="none" stroke="rgba(255,255,255,.12)" strokeWidth="3.5" />
          <circle cx="22" cy="22" r={r} fill="none" stroke={color} strokeWidth="3.5"
            strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
            transform="rotate(-90 22 22)" style={{ transition: 'stroke-dashoffset 1.2s ease' }} />
        </svg>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ fontSize: '0.68rem', fontWeight: 900, color: 'white', lineHeight: 1 }}>{taxa}%</span>
        </div>
      </div>
      <span style={{ fontSize: '0.48rem', opacity: 0.75, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'white', marginTop: 2, whiteSpace: 'nowrap' }}>
        Presença
      </span>
    </div>
  );
}

function KPICard({ icon: Icon, label, value, sub, color, trend }: {
  icon: any; label: string; value: string | number; sub?: string; color: string; trend?: { value: string; up: boolean };
}) {
  return (
    <div className="muni-kpi">
      <div className="muni-kpi-header">
        <div className="muni-kpi-icon" style={{ background: `${color}12`, color }}><Icon size={18} /></div>
        <span className="muni-kpi-label">{label}</span>
      </div>
      <div className="muni-kpi-value">{value}</div>
      <div className="muni-kpi-footer">
        {trend && (
          <span className={`muni-kpi-trend ${trend.up ? 'up' : 'down'}`}>
            {trend.up ? <TrendingUp size={11} /> : <TrendingDown size={11} />} {trend.value}
          </span>
        )}
        {sub && <span className="muni-kpi-sub">{sub}</span>}
      </div>
    </div>
  );
}

function EscolaRow({ escola, index }: { escola: any; index: number }) {
  const statusCfg: Record<string, { bg: string; color: string; label: string }> = {
    critico: { bg: '#fef2f2', color: '#dc2626', label: 'Critico' },
    atencao: { bg: '#fffbeb', color: '#d97706', label: 'Atencao' },
    normal: { bg: '#f0fdf4', color: '#16a34a', label: 'Normal' },
  };
  const s = statusCfg[escola.statusOperacional] || statusCfg.normal;
  const barWidth = Math.min(escola.taxaPresenca, 100);
  const barColor = escola.taxaPresenca >= 85 ? '#10b981' : escola.taxaPresenca >= 70 ? '#f59e0b' : '#ef4444';

  return (
    <div className="muni-escola-row">
      <div className="muni-escola-rank" style={{ color: index < 3 ? '#1e40af' : undefined, fontWeight: index < 3 ? 800 : 600 }}>
        {index + 1}
      </div>
      <div className="muni-escola-info">
        <div className="muni-escola-name">{escola.escola?.nome || escola.nome}</div>
        <div className="muni-escola-meta">
          <span>{escola.totalAlunos} alunos</span>
          <span>{escola.presentes} pres.</span>
          <span>{escola.ausentes} aus.</span>
        </div>
        <div className="muni-escola-bar-track">
          <div className="muni-escola-bar-fill" style={{ width: `${barWidth}%`, background: barColor }} />
        </div>
      </div>
      <div className="muni-escola-taxa" style={{ color: barColor }}>{escola.taxaPresenca}%</div>
      <span className="muni-escola-badge" style={{ background: s.bg, color: s.color }}>{s.label}</span>
    </div>
  );
}

function AlertCard({ alerta }: { alerta: any }) {
  const isCritica = alerta.severidade === 'CRITICA';
  return (
    <div className={`muni-alert ${isCritica ? 'critica' : 'alta'}`}>
      <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
      <div>
        <div className="muni-alert-title">{alerta.tipoIncidente?.replace(/_/g, ' ')}</div>
        <div className="muni-alert-meta">
          {alerta.escola?.nome} {alerta.aluno?.nome ? `· ${alerta.aluno.nome}` : ''}
          {' · '}{new Date(alerta.dataHoraReferencia).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
        </div>
      </div>
    </div>
  );
}

function StatItem({ label, value, color }: { label: string; value: number | string; color?: string }) {
  return (
    <div className="muni-stat-item">
      <span>{label}</span>
      <strong style={color ? { color } : undefined}>{value}</strong>
    </div>
  );
}

function SectionCard({ title, icon: Icon, iconColor, children, action, className }: {
  title: string; icon: any; iconColor: string; children: React.ReactNode; action?: React.ReactNode; className?: string;
}) {
  return (
    <div className={`muni-section ${className || ''}`}>
      <div className="muni-section-header">
        <h3><Icon size={18} style={{ color: iconColor }} /> {title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}

/* ─── Dashboard Principal ─── */

export function DashboardMunicipio() {
  const user = useAuthStore((s) => s.user);
  const checkAuth = useAuthStore((s) => s.checkAuth);
  const [attemptedRefresh, setAttemptedRefresh] = useState(false);
  const municipioId = user?.municipioId;
  const [analyticsData, setAnalyticsData] = useState<any>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [showAllEscolas, setShowAllEscolas] = useState(false);

  useEffect(() => {
    if (!municipioId && !attemptedRefresh) {
      setAttemptedRefresh(true);
      checkAuth();
    }
  }, [municipioId, attemptedRefresh, checkAuth]);

  const { data, isLoading, error, refetch, dataUpdatedAt, isFetching } = useDashboardApi(
    `dashboard-municipio-${municipioId}`,
    municipioId ? `/municipios/${municipioId}/dashboard` : '',
  );

  // Live status hook
  const { isOnline } = useLiveStatus();

  // Carregar analytics separadamente — com refresh periódico (5 min)
  const fetchAnalytics = useCallback(() => {
    if (!municipioId) return;
    setAnalyticsLoading(true);
    const token = localStorage.getItem('ac_token');
    const base = import.meta.env.VITE_API_URL || '/api/v1';
    fetch(`${base}/municipios/${municipioId}/dashboard/analytics?dias=30`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => setAnalyticsData(d))
      .catch(() => {})
      .finally(() => setAnalyticsLoading(false));
  }, [municipioId]);

  useEffect(() => {
    fetchAnalytics();
    const interval = setInterval(fetchAnalytics, ANALYTICS_INTERVAL * 1000);
    return () => clearInterval(interval);
  }, [fetchAnalytics]);

  if (!municipioId) {
    if (!attemptedRefresh) {
      return <LoadingSpinner label="Identificando município..." />;
    }
    return (
      <div className="admin-page muni-dashboard flex items-center justify-center p-8 text-center" style={{ minHeight: '60vh' }}>
        <div className="bg-card p-6 rounded-2xl border border-border max-w-md mx-auto shadow-sm">
          <Landmark size={36} className="mx-auto text-amber-500 mb-3" />
          <h2 className="text-base font-bold text-foreground mb-1">Município em sincronização</h2>
          <p className="text-xs text-muted-foreground mb-4">
            Não foi possível identificar o vínculo municipal da sua conta automaticamente.
          </p>
          <button
            type="button"
            onClick={() => checkAuth()}
            className="btn-primary text-xs px-4 py-2 rounded-lg font-medium cursor-pointer inline-flex items-center gap-2"
          >
            <RefreshCw size={13} /> Sincronizar Perfil
          </button>
        </div>
      </div>
    );
  }
  if (isLoading) return <LoadingSpinner label="Carregando Dashboard Municipal..." />;
  if (error) return <ErrorPanel />;

  const c = data?.cards || {};
  const chamadas = data?.operacao?.chamadasDoDia || {};
  const notif = data?.analitico?.resumoNotificacoes || {};
  const escolas = data?.operacao?.statusEscolas || [];
  const ranking = data?.analitico?.rankingPresenca || [];
  const alertas = data?.alertas?.criticos || [];
  const feed = data?.operacao?.feed || [];
  const panorama = data?.panoramaGeral || {};
  const familias = data?.familias;
  const totalAlunos = c.totalAlunosPrevistos || 0;
  const presentes = (c.totalPresentes || 0) + (c.totalAtrasados || 0);
  const ausentes = c.totalAusentes || 0;
  const taxaGlobal = totalAlunos > 0 ? Number(((presentes / totalAlunos) * 100).toFixed(1)) : 0;
  const semChamada = Math.max(0, totalAlunos - presentes - ausentes);
  const alunosEmRisco = analyticsData?.alunosRisco?.length || alertas.filter((a: any) => a.tipoIncidente === 'AUSENCIA_PROLONGADA').length || 0;
  const escolasVisiveis = showAllEscolas ? escolas : escolas.slice(0, 8);

  return (
    <div className={`admin-page muni-dashboard${(() => { try { return sessionStorage.getItem('ac_cockpit_embed') === '1' ? '' : ' animate-fadeIn'; } catch { return ' animate-fadeIn'; } })()}`}>

      <header className="muni-hero">
        <div className="muni-hero-bg-circle c1" /><div className="muni-hero-bg-circle c2" />
        <div className="muni-hero-content">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, width: '100%' }}>
            {/* Lado Esquerdo: Identidade do Município */}
            <div style={{ minWidth: 0, flexShrink: 1 }}>
              <div className="muni-hero-badge" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Landmark size={10} /> REDE VINCULADA: {data?.municipio?.estado?.rede?.nome || 'REDE MUNICIPAL'}
              </div>
              <h1 className="muni-hero-title">{data?.municipio?.nome || 'Dashboard Municipal'}</h1>
              <p className="muni-hero-subtitle">
                {data?.municipio?.estado?.nome} ({data?.municipio?.estado?.sigla}) — {new Date().toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}
                <span className="muni-hero-live">
                  <span className={`muni-hero-live-dot ${isOnline ? (isFetching ? 'fetching' : 'online') : 'offline'}`} />
                  {!isOnline ? 'Offline' : isFetching ? 'Atualizando' : 'Ao vivo'}
                  <span className="muni-hero-live-track"><span key={dataUpdatedAt} className="muni-hero-live-fill" /></span>
                  <button type="button" className={`muni-hero-live-btn ${isFetching ? 'spinning' : ''}`} onClick={() => { refetch(); fetchAnalytics(); }} title="Atualizar agora"><RefreshCw size={10} /></button>
                </span>
              </p>
            </div>

            {/* Lado Direito: KPIs em Linha Única + Gauge de Presença */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
              <div className="muni-hero-stats-inline">
                <HeroStat icon={Building2} value={c.totalEscolas || 0} label="Escolas" color="#34d399" />
                <HeroStat icon={GraduationCap} value={totalAlunos.toLocaleString('pt-BR')} label="Alunos" color="#818cf8" />
                <HeroStat icon={CheckCircle2} value={presentes.toLocaleString('pt-BR')} label="Presentes" color="#4ade80" />
                <HeroStat icon={AlertTriangle} value={ausentes} label="Ausentes" color="#fbbf24" />
                <HeroStat icon={Users} value={familias?.totalResponsaveis || 0} label="Famílias" color="#f0abfc" />
              </div>

              <PresencaGauge taxa={taxaGlobal} />
            </div>
          </div>
        </div>
      </header>

      <div className="muni-kpi-grid">
        <KPICard icon={CheckCircle2} label="Chamadas Realizadas" value={chamadas.concluidas || 0}
          sub={`${chamadas.total || 0} total hoje`} color="#10b981" />
        <KPICard icon={UserX} label="Risco de Evasao" value={alunosEmRisco}
          sub={alunosEmRisco > 0 ? 'alunos c/ baixa frequencia' : 'nenhum aluno em risco'}
          color="#dc2626" />
        <KPICard icon={Megaphone} label="Pais Notificados" value={notif.entregues || notif.enviadas || 0}
          sub={`${notif.lidas || 0} lidas · ${notif.taxaEngajamento || notif.taxaLeitura || 0}% engajamento`} color="#6366f1" />
        <KPICard icon={AlertTriangle} label="Inconsistencias" value={c.totalInconsistencias || 0}
          sub={`${c.totalEscolasComProblema || 0} escolas c/ alerta`} color="#ef4444" />
      </div>

      <div className="muni-shell">
        <div className="muni-main">
          <div className="muni-charts-duo">
            <FamiliasPieChart familias={familias} />
            <NotificacoesFamiliasChart notificacoes={notif} />
          </div>
          <SectionCard title="Status por Escola" icon={Building2} iconColor="#1e40af"
            action={escolas.length > 8 && (
              <button className="muni-btn-link" onClick={() => setShowAllEscolas(!showAllEscolas)}>
                {showAllEscolas ? <><ChevronUp size={14} /> Recolher</> : <><ChevronDown size={14} /> Ver todas ({escolas.length})</>}
              </button>
            )}>
            {escolas.length === 0 ? (
              <div className="muni-empty"><Building2 size={28} /><p>Nenhuma escola cadastrada</p></div>
            ) : (
              <div className="muni-escolas-list">
                {escolasVisiveis.map((e: any, i: number) => <EscolaRow key={e.escola?.id || i} escola={e} index={i} />)}
              </div>
            )}
          </SectionCard>

          {/* Ranking de Presenca */}
          {ranking.length > 0 && (
            <SectionCard title="Ranking de Presenca" icon={BarChart3} iconColor="#6366f1">
              <div className="muni-ranking-table-wrap">
                <table className="muni-ranking-table">
                  <thead>
                    <tr><th>#</th><th>Escola</th><th>Matric.</th><th>Presenca</th><th>Taxa</th></tr>
                  </thead>
                  <tbody>
                    {ranking.map((r: any, i: number) => {
                      const tc = r.taxaPresenca >= 85 ? '#10b981' : r.taxaPresenca >= 70 ? '#f59e0b' : '#ef4444';
                      return (
                        <tr key={r.escola?.id || i}>
                          <td className={i < 3 ? 'muni-rank-top' : ''}>{i + 1}o</td>
                          <td className="muni-rank-name">{r.escola?.nome}</td>
                          <td>{r.matriculados}</td>
                          <td>{r.totalComPresenca}</td>
                          <td><strong style={{ color: tc }}>{r.taxaPresenca}%</strong></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </SectionCard>
          )}

          {/* Alunos em Risco */}
          {analyticsData?.alunosRisco?.length > 0 && (
            <SectionCard title="Alunos em Risco de Evasao" icon={AlertTriangle} iconColor="#dc2626">
              <div className="muni-risk-list">
                {analyticsData.alunosRisco.map((a: any, i: number) => (
                  <div key={i} className="muni-risk-row">
                    <div className="muni-risk-rank">{i + 1}</div>
                    <div className="muni-risk-info">
                      <span className="muni-risk-name">{a.nome}</span>
                      <span className="muni-risk-escola">{a.escola}</span>
                    </div>
                    <span className="muni-risk-badge">{a.faltas} faltas</span>
                  </div>
                ))}
              </div>
            </SectionCard>
          )}

          <SectionCard title="Feed — Ultimos Eventos" icon={Activity} iconColor="#2563eb"
            action={<button type="button" className="muni-btn-link" onClick={() => refetch()}><RefreshCw size={14} /> Atualizar</button>}>
            {feed.length === 0 ? (
              <div className="muni-empty"><Activity size={28} /><p>Nenhum evento registrado</p></div>
            ) : (
              <div className="muni-feed-wrap">
                <table className="muni-feed-table">
                  <thead>
                    <tr><th>Horario</th><th>Tipo</th><th>Aluno</th><th>Escola</th><th>Status</th></tr>
                  </thead>
                  <tbody>
                    {feed.slice(0, 20).map((e: any) => (
                      <tr key={e.id}>
                        <td>{new Date(e.dataHora).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</td>
                        <td><span className={`muni-feed-tipo ${e.tipo === 'ENTRADA' ? 'entrada' : 'saida'}`}>{e.tipo === 'ENTRADA' ? 'Entrada' : 'Saida'}</span></td>
                        <td>{e.aluno?.nome || '—'}</td>
                        <td>{e.escola?.nome || '—'}</td>
                        <td><span className={`muni-feed-status ${e.status === 'VALIDO' ? 'ok' : 'err'}`}>{e.status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </SectionCard>
        </div>

        <aside className="muni-sidebar">
          <PainelAlertaUrgente data={data} familias={familias} />

          {familias && <AlcancePoliticoCard familias={familias} totalAlunos={totalAlunos} />}

          <SectionCard title="Alertas Criticos" icon={AlertTriangle} iconColor="#dc2626">
            {alertas.length === 0 ? (
              <div className="muni-empty success">
                <CheckCircle2 size={28} style={{ color: '#10b981' }} />
                <p>Nenhum alerta critico ativo</p>
              </div>
            ) : (
              <div className="muni-alerts-list">
                {alertas.slice(0, 8).map((a: any) => <AlertCard key={a.id} alerta={a} />)}
              </div>
            )}
          </SectionCard>

          {/* Notificacoes */}
          <SectionCard title="Notificacoes Hoje" icon={Megaphone} iconColor="#6366f1">
            <StatItem label="Total Disparadas" value={notif.total || 0} />
            <StatItem label="Entregues" value={notif.entregues || 0} color="#10b981" />
            <StatItem label="Lidas" value={notif.lidas || 0} color="#059669" />
            <StatItem label="Falhas" value={notif.falhas || 0} color="#dc2626" />
            <StatItem label="Pendentes" value={notif.pendentes || 0} color="#f59e0b" />
            <StatItem label="Taxa de Leitura" value={`${notif.taxaLeitura || 0}%`} color="#7c3aed" />
          </SectionCard>

          {/* Panorama Territorial */}
          {panorama.totalEscolasTerritorio > 0 && (
            <SectionCard title="Panorama Territorial" icon={Eye} iconColor="#0891b2">
              <StatItem label="Total no Territorio" value={panorama.totalEscolasTerritorio} />
              <StatItem label="Rede Municipal" value={panorama.porRede?.municipal || 0} color="#1e40af" />
              <StatItem label="Rede Estadual" value={panorama.porRede?.estadual || 0} color="#1e40af" />
              <StatItem label="Rede Federal" value={panorama.porRede?.federal || 0} color="#7c3aed" />
              <StatItem label="Rede Particular" value={panorama.porRede?.particular || 0} color="#6b7280" />
              <StatItem label="Total Alunos" value={(panorama.totalAlunosTerritorio || 0).toLocaleString('pt-BR')} />
            </SectionCard>
          )}
        </aside>
      </div>
    </div>
  );
}
